// SF6 — Order Defect Rate Monitor.
//
// Amazon's official figure, read from the account health report. Not an
// estimate, and deliberately not responsive to the window control above:
// Amazon defines the reporting period for account health, and reinterpreting it
// into the seller's chosen dates would produce a number matching nothing they
// could check in Seller Central.
//
// The composition is the useful part. Claims, chargebacks and negative feedback
// are different problems with different fixes, so a single rate hides which one
// is driving it. Only feedback can be traced to an order, so only it has a
// product view — and that view is not the whole defect rate.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo, FiMessageSquare } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderFulfillment.module.css';
import { getDefectRate } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const count = (value) =>
  value === null || value === undefined ? '—' : inr.format(value);

const percent = (ratio, digits = 3) =>
  ratio === null || ratio === undefined ? '—' : `${(ratio * 100).toFixed(digits)}%`;

const DefectRate = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getDefectRate(token));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const channels = data?.channels || [];
  const feedback = data?.feedback;
  const window = channels[0]?.window;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Order defect rate</h2>
        {window?.start && (
          <span className={local.amazonTag}>
            Amazon’s window · {window.start} to {window.end}
          </span>
        )}
      </div>
      <p className={local.muted}>
        Amazon’s own figure, split by channel because your exposure differs
        between orders Amazon ships and orders you ship.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Account health report not synced' : 'Could not load the defect rate'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && data && channels.length === 0 && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>Amazon has not reported this yet</strong>
            <div>The account health report synced without a defect rate in it.</div>
          </div>
        </div>
      )}

      {!error && channels.map((channel) => (
        <div key={channel.channel} className={local.defectBlock}>
          <div className={local.defectHead}>
            <span className={local.defectLabel}>{channel.label}</span>
            <span className={channel.within_target ? local.defectOk : local.defectBad}>
              {percent(channel.rate)}
            </span>
            <span className={local.muted}>
              target under {percent(channel.target, 0)} ·{' '}
              {channel.within_target ? 'within' : 'over'} ·{' '}
              {count(channel.orders_with_defects)} defects in {count(channel.orders)} orders
            </span>
          </div>
          <div className={local.componentRow}>
            {channel.components.map((part) => (
              <div key={part.key} className={local.componentTile}>
                <div className={local.todayLabel}>
                  {part.label}
                  {!part.has_order_detail && (
                    <span className={local.estTag} title="Amazon reports no order behind these">
                      count only
                    </span>
                  )}
                </div>
                <div className={local.todayValue}>{count(part.count)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {!error && feedback && feedback.negative > 0 && (
        <>
          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              <FiMessageSquare /> What buyers actually said
              <span className={local.groupCount}>{count(feedback.negative)} negative</span>
            </h3>
          </div>

          {/* Themes need enough comments for a pattern to exist. Below the
              threshold the comments are shown as written instead, because a
              theme drawn from a handful would be invented. */}
          {!feedback.themes_available && (
            <p className={local.muted}>
              Not grouped into themes — there are only {count(feedback.negative)},
              and a theme drawn from that few would be a pattern invented rather
              than found. They are short enough to read.
            </p>
          )}

          <div className={local.comments}>
            {feedback.comments.map((entry, index) => (
              <div key={`${entry.date}-${index}`} className={local.comment}>
                <span className={local.commentRating}>{entry.rating}★</span>
                <span className={local.commentText}>{entry.comment || 'No comment left.'}</span>
                <span className={local.commentDate}>{entry.date}</span>
                {entry.answered && <span className={local.measuredTag}>replied</span>}
              </div>
            ))}
          </div>

          {data.products?.length > 0 && (
            <>
              <div className={local.groupHead}>
                <h3 className={local.groupTitle}>
                  Products drawing negative feedback
                  <span className={local.groupCount}>feedback only, not the full rate</span>
                </h3>
              </div>
              <div className={local.tableScroll}>
                <table className={local.table}>
                  <thead>
                    <tr>
                      <th>ASIN</th>
                      <th className={local.numeric}>Negative feedback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.products.map((row) => (
                      <tr key={row.asin}>
                        <td className={local.drillId}>{row.asin}</td>
                        <td className={local.numeric}>{count(row.negative)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DefectRate;
